import { Create, EditBase, Resource, Tab, useRedirect } from "react-admin";
import { BusForm } from "../details/vehicles/BusForm";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useEffect } from "react";
import { Box } from "@mui/material";
import { Column } from "../misc/Column";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { Tabs } from "../misc/Tabs";
import { VehicleCalendar } from "../details/vehicles/VehicleCalendar";
import { grants } from "../backoffice.access_control";
import { VehicleDetailsHeader } from "../details/vehicles/VehicleDetailsHeader";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { AccessDenied } from "../misc/AccessDenied";
import { Bus } from "../providers/vehiclesProvider";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { DefaultList } from "../misc/DefaultList";
import { VehicleDataCard } from "../details/vehicles/VehicleDataCard";

function CreateBusForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();
  useEffect(() => {
    if (!grants.has("Vehicle:create") && drivingSchoolId) {
      redirect(`/drivingSchools/${drivingSchoolId}/fleet#buses`);
    }
  }, []);

  if (!grants.has("Vehicle:create")) {
    return <AccessDenied />;
  }

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create<Bus>
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: ({ name }) => {
          // eslint-disable-next-line react-compiler/react-compiler
          window.onNavigation = () => {
            delete window.onNavigation;
            showSuccessToast(`Bus ${name} erfolgreich gespeichert`);
          };
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#buses`);
        },
      }}
    >
      <BusForm mode="create" sx={{ padding: overviewCardStyle.padding }} />
    </Create>
  );
}

function BusDetails() {
  const redirect = useRedirect();
  const [{ drivingSchoolId }] = useAutovioContext();
  const bus = useRecordAndSwitchDrivingSchoolIfNecessary<Bus>((it) => it.drivingSchoolId);

  if (!bus) {
    return <LoadingIndicator />;
  }

  return (
    <EditBase
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert");
          redirect(`/drivingSchools/${drivingSchoolId}/fleet#buses`);
        },
      }}
    >
      <Tabs
        sx={{ ...overviewCardStyle, padding: { xs: "5px 16px 16px", sm: "10px 30px" } }}
        header={<VehicleDetailsHeader />}
        mobileSelectAriaLabel="Fahrzeugbereich"
        syncWithLocation={false}
        syncWithHash
        separateCards
      >
        <Tab label="Kalender" value="calendar">
          <VehicleCalendar />
        </Tab>
        {grants.has("Vehicle:edit") && (
          <Tab label="Fahrzeug-Daten" value="data">
            <VehicleDataCard>
              <BusForm mode="edit" />
            </VehicleDataCard>
          </Tab>
        )}
      </Tabs>
    </EditBase>
  );
}

export const busesResource = (
  <Resource
    key="buses"
    name="buses"
    list={DefaultList}
    create={CreateBusForm}
    edit={BusDetails}
    options={{
      label: "Busse",
      createTitle: "Neuer Bus",
      noEntriesText: "Noch kein Bus hinterlegt.",
      noMatchingEntriesText: "Keine passenden Busse gefunden.",
    }}
    recordRepresentation={(record: Bus) => `${record.name} (${record.plateNumber})`}
  />
);
